import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SearchProvider } from './contexts/SearchContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import AiAssistantFab from './components/AiAssistantFab';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Producten from './pages/Producten';
import ProductDetail from './pages/ProductDetail';
import Leveranciers from './pages/Leveranciers';
import Verkoopdata from './pages/Verkoopdata';
import Attributie from './pages/Attributie';
import Koppelingen from './pages/Koppelingen';
import Tickets from './pages/Tickets';
import TicketsInstellingen from './pages/TicketsInstellingen';
import WooCommerce from './pages/WooCommerce';
import Parameters from './pages/Parameters';
import Inkoopvoorstellen from './pages/Inkoopvoorstellen';
import Inkooporders from './pages/Inkooporders';
import Magazijn from './pages/Magazijn';
import RetourAanmelden from './pages/RetourAanmelden';
import Migratie from './pages/Migratie';
import Gebruikers from './pages/Gebruikers';
import MarketingKalender from './pages/MarketingKalender';
import MarketingPage from './pages/marketing/MarketingPage';
import MarketingInstagram from './pages/marketing/MarketingInstagram';
import MarketingPopupPanel from './pages/marketing/MarketingPopupPanel';
import Klanten360 from './pages/Klanten360';
import './App.css';

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const errorBoundaryKey = location.pathname;

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route
        path="/retour"
        element={
          <ErrorBoundary>
            <RetourAanmelden />
          </ErrorBoundary>
        }
      />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Dashboard />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/producten" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Producten />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/producten/:id" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <ProductDetail />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/leveranciers" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Leveranciers />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inkooporders" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Inkooporders />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/magazijn" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Magazijn />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inkoopvoorstellen" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Inkoopvoorstellen />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary key={errorBoundaryKey}>
                <MarketingPage />
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<MarketingKalender />} />
        <Route path="instagram" element={<MarketingInstagram />} />
        <Route path="popup" element={<MarketingPopupPanel />} />
      </Route>
      <Route path="/klanten" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Klanten360 />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/verkoopdata" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Verkoopdata />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/attributie" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Attributie />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/koppelingen" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Koppelingen />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Tickets />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/tickets/instellingen" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <TicketsInstellingen />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/woocommerce" element={<Navigate to="/koppelingen" replace />} />
      <Route path="/parameters" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Parameters />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/migratie" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Migratie />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/gebruikers" element={
        <ProtectedRoute requireAdmin>
          <Layout>
            <ErrorBoundary key={errorBoundaryKey}>
              <Gebruikers />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SearchProvider>
          <AppRoutes />
          <InstallPrompt />
          <AiAssistantFab />
        </SearchProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
